import { useEffect } from 'react';
import api from '../services/api';

declare global {
  interface Window {
    chatwootSettings: any;
    chatwootSDK: any;
  }
}

/**
 * Chatwoot live chat widget.
 * Loads Chatwoot settings from the backend and injects the widget script.
 * Mount this component once in your app layout (e.g., App.tsx).
 */
const ChatwootWidget = () => {
  useEffect(() => {
    let script: HTMLScriptElement | null = null;

    const loadChatwoot = async () => {
      try {
        // Fetch Chatwoot settings from backend (admin-configured)
        const res = await api.get('/api/admin/config/chatwoot-settings');
        const settings = res.data;

        const baseUrl = settings?.chatwoot_base_url;
        const token = settings?.chatwoot_website_token;

        if (!baseUrl || !token) {
          return; // Not configured — don't load widget
        }

        // Don't load twice
        if (document.getElementById('chatwoot-sdk')) return;

        window.chatwootSettings = {
          hideMessageBubble: false,
          position: 'right',
          locale: 'en',
          type: 'standard',
        };

        script = document.createElement('script');
        script.id = 'chatwoot-sdk';
        script.src = `${baseUrl}/packs/js/sdk.js`;
        script.async = true;
        script.defer = true;

        script.onload = () => {
          window.chatwootSDK?.run({
            websiteToken: token,
            baseUrl: baseUrl,
          });
        };

        document.head.appendChild(script);
      } catch {
        // Silently fail if settings can't be loaded (e.g., not logged in)
      }
    };

    loadChatwoot();

    return () => {
      // Cleanup on unmount
      if (script && script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  return null; // Renders nothing — purely side-effect
};

export default ChatwootWidget;
